/**
 * Clean Chart toggle + configuration.
 *
 * The button is a plain on/off switch (aria-pressed) for cleanChartStore.active:
 * press it and the chart cleans, press it again and the chart is exactly as it
 * was. The caret beside it opens the list of what Clean hides — real
 * checkboxes, one per group, plus All / None / Restore defaults — so choosing
 * what Clean hides is deliberate rather than something you trip over.
 *
 * Styles live in CleanToggle.css under clean-* classes only. This used to borrow
 * the levels-* classes from LevelsToggle.css; the Levels panel is being
 * redesigned independently and Clean must not change shape when it does.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Eye, EyeOff, X, ChevronDown, RotateCcw } from 'lucide-react';
import {
  useCleanChartStore,
  CLEAN_TARGETS,
  CLEAN_TARGET_LABELS,
} from '../store/cleanChartStore';
import './CleanToggle.css';

interface CleanToggleProps {
  /** Whether clean mode is currently on. */
  active: boolean;
  /** Called when the user presses the Clean button itself. */
  onToggle: () => void;
}

/** One-line tooltip for the main button, describing what a click will do. */
export function cleanButtonTitle(active: boolean, hiddenCount: number, total: number): string {
  if (active) return `Clean is ON (hiding ${hiddenCount} of ${total} groups). Click to show your chart again.`;
  if (hiddenCount === 0) return 'Clean is OFF. Nothing is ticked in the ▾ menu, so Clean would hide nothing.';
  return `Clean is OFF. Click to hide the ${hiddenCount} groups ticked in the ▾ menu.`;
}

const CleanToggle: React.FC<CleanToggleProps> = ({ active, onToggle }) => {
  const hides = useCleanChartStore((s) => s.hides);
  const setTarget = useCleanChartStore((s) => s.setTarget);
  const setAll = useCleanChartStore((s) => s.setAll);
  const resetToDefaults = useCleanChartStore((s) => s.resetToDefaults);

  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const hiddenCount = CLEAN_TARGETS.filter((t) => hides[t]).length;

  const handleToggleExpand = useCallback(() => {
    if (!isExpanded && containerRef.current) {
      // Positioned off the real button rect because the dropdown is portalled to
      // body — inside the action bar it would be clipped by the chart container.
      // Clamped so it never runs off the right edge on a phone.
      const rect = containerRef.current.getBoundingClientRect();
      const width = Math.min(300, window.innerWidth - 16);
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
      setDropdownPos({ top: rect.bottom + 4, left });
    }
    setIsExpanded((v) => !v);
  }, [isExpanded]);

  const handleClose = useCallback(() => setIsExpanded(false), []);

  useEffect(() => {
    if (!isExpanded) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !containerRef.current?.contains(target) &&
        !dropdownRef.current?.contains(target)
      ) {
        setIsExpanded(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsExpanded(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isExpanded]);

  const Icon = active ? EyeOff : Eye;

  return (
    <div ref={containerRef} className={`clean-toggle-group ${active ? 'is-on' : ''}`}>
      <button
        type="button"
        className={`action-bar-btn clean-main-btn ${active ? 'active' : ''}`}
        onClick={onToggle}
        aria-pressed={active}
        aria-label="Clean"
        data-testid="clean-toggle"
        title={cleanButtonTitle(active, hiddenCount, CLEAN_TARGETS.length)}
      >
        <Icon size={14} className="action-bar-icon" />
        <span className="action-bar-label">Clean</span>
        {active && <span className="clean-on-pill" aria-hidden="true">ON</span>}
      </button>
      <button
        type="button"
        className={`action-bar-btn clean-config-btn ${active ? 'active' : ''}`}
        onClick={handleToggleExpand}
        aria-label="Choose what Clean hides"
        aria-expanded={isExpanded}
        aria-haspopup="dialog"
        data-testid="clean-config"
        title={`Choose what Clean hides (${hiddenCount}/${CLEAN_TARGETS.length} ticked)`}
      >
        <ChevronDown size={12} className="action-bar-icon" />
      </button>

      {isExpanded &&
        createPortal(
          <div
            ref={dropdownRef}
            className="clean-menu"
            role="dialog"
            aria-label="Clean hides"
            data-testid="clean-menu"
            // zIndex is required, not decorative: portalled to body at auto, the
            // chart canvas and trading panel would paint over it.
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              zIndex: 10000,
            }}
          >
            <div className="clean-menu-header">
              <span>
                Clean hides <span className="clean-menu-count">{hiddenCount}/{CLEAN_TARGETS.length}</span>
              </span>
              <button type="button" className="clean-menu-close" onClick={handleClose} aria-label="Close">
                <X size={14} />
              </button>
            </div>

            <div className="clean-menu-status" data-testid="clean-menu-status">
              {active ? 'Clean is ON: changes apply now.' : 'Clean is OFF: these hide when you press Clean.'}
            </div>

            <div className="clean-menu-actions">
              <button type="button" className="clean-menu-btn" onClick={() => setAll(true)}>All</button>
              <button type="button" className="clean-menu-btn" onClick={() => setAll(false)}>None</button>
              <button
                type="button"
                className="clean-menu-btn"
                onClick={resetToDefaults}
                data-testid="clean-restore-defaults"
                title="Restore the default list (everything ticked)"
              >
                <RotateCcw size={11} /> Restore defaults
              </button>
            </div>

            <div className="clean-menu-list">
              {CLEAN_TARGETS.map((target) => {
                const { label, description } = CLEAN_TARGET_LABELS[target];
                const id = `clean-target-${target}`;
                return (
                  <label key={target} className="clean-menu-item" htmlFor={id} title={description}>
                    <input
                      id={id}
                      type="checkbox"
                      checked={hides[target]}
                      onChange={(e) => setTarget(target, e.target.checked)}
                      data-testid={id}
                    />
                    <span className="clean-menu-item-text">
                      <span className="clean-menu-item-label">{label}</span>
                      <span className="clean-menu-item-desc">{description}</span>
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="clean-menu-footer">
              Turning Clean off always restores your chart exactly. Levels you switch on while Clean is ON appear when you turn it off.
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CleanToggle;
